import { type ReactNode, useEffect, useRef } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, MemoryRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { GenieAccessRoute, RoleProtectedRoute } from "@/auth";
import { useAuth } from "@/auth/AuthProvider";
import { GenieExplainerOverlay } from "@/components/explainer/GenieExplainerOverlay";
import AppLayout from "@/components/layout/AppLayout";
import ScrollToTop from "@/components/layout/ScrollToTop";
import SiteMetadataManager from "@/components/seo/SiteMetadataManager";
import { Toaster } from "@/components/ui/sonner";
import { WidgetHider } from "@/components/widget/WidgetHider";
import {
  BrainProvider,
  CurrencyProvider,
  ExplainerProvider,
  InstallAppBannerProvider,
  NavModeProvider,
  SidebarProvider,
  SubscriptionProvider,
  useInstallAppBanner,
} from "@/contexts";
import { OnboardingProvider } from "@/contexts/voice-onboarding/OnboardingContext";
import { queryClient } from "@/lib/queryClient";
import {
  KeepGenieLink,
  PublicActivationPage,
  SlackInstallCallback,
  SlackOwnAppInstall,
  TeamsInstallCallback,
  SmartActivationLink,
} from "@/pages/activation-interfaces";
import AdminLanding from "@/pages/Admin";
import DevDocs from "@/pages/Admin/DevDocs";
import ImageGeneratorAdmin from "@/pages/Admin/ImageGeneratorAdmin";
import MarketplaceAdmin from "@/pages/Admin/MarketplaceAdmin";
import PageViewAnalyticsPage from "@/pages/Admin/PageViewAnalytics";
import AiUsagePage from "@/pages/Admin/AiUsage";
import RecentConversations from "@/pages/Admin/RecentConversations";
import SystemEmailsPage from "@/pages/Admin/SystemEmails";
import { LiveAgentPage } from "@/pages/agent-interfaces";
import EmailWidgetPage from "@/pages/agent-interfaces/EmailWidgetPage";
import PrivateDemoPage from "@/pages/agent-interfaces/PrivateDemoPage";
import { KnowledgeBasePage, VoiceAgentsPage } from "@/pages/AgentManagement";
import ApplicationManagement from "@/pages/ApplicationManagement";
import { ConsumerSignIn, ForgotPasswordPage, SignIn, SignUp, UpdatePasswordPage } from "@/pages/Auth";
import AuthConfirm from "@/pages/Auth/AuthConfirm";
import BusinessOnboardingPage from "@/pages/Auth/BusinessOnboardingPage";
import ConsumerOnboardingPage from "@/pages/Auth/ConsumerOnboardingPage";
import CreatingGeniePage from "@/pages/Auth/CreatingGeniePage";
import PublicBusinessPage from "@/pages/Business/PublicBusinessPage";
import {
  InternalInformationDeliveryPage,
  ProductSupportPage,
  SalesLeadCapturePage,
  VoiceExperiencesPage,
} from "@/pages/Capabilities";
import { ClubAssociationEditPage } from "@/pages/CustomerOnboarding";
import Dashboard from "@/pages/Dashboard/index";
import { ConfirmationPageDev, WidgetTesterDev } from "@/pages/Dev";
import DiscoveryPage from "@/pages/Discovery";
import DemoPage from "@/pages/Discovery/DemoPage";
import { GeneralDataLibrary } from "@/pages/Documents";
import NotFound from "@/pages/Errors/NotFound";
import { AgentStudioPage } from "@/pages/genie/studio";
import {
  EcommerceExample,
  EducationExample,
  FinancialExample,
  FitnessExample,
  GenieExamplesManagement,
  HealthcareExample,
  LegalExample,
  RealEstateExample,
  RestaurantExample,
  TravelExample,
} from "@/pages/GenieExamples";
import AskByEmailExample from "@/pages/GenieExamples/AskByEmailExample";
import { HelpGuidePage, HelpHubPage } from "@/pages/help-hub";
import ContactPage from "@/pages/Home/ContactPage";
import HowGenieWorks from "@/pages/Home/HowGenieWorks";
import PaymentSuccess from "@/pages/Home/PaymentSuccess";
import PricingPage from "@/pages/Home/Pricing";
import SmartHomePage from "@/pages/Home/SmartHomePage";
import TalkToGeniePage from "@/pages/Home/TalkToGeniePage";
import WhatCanGeniesDoPublic from "@/pages/Home/WhatCanGeniesDoPublic";
import CaptureDemo from "@/pages/internal/CaptureDemo";
import ChatDemo from "@/pages/internal/ChatDemo";
import LoadingDemo from "@/pages/internal/LoadingDemo";
import RehearseDemo from "@/pages/internal/RehearseDemo";
import SpotlightDemo from "@/pages/internal/SpotlightDemo";
import SwitcherDemo from "@/pages/internal/SwitcherDemo";
import PublicDocumentEditPage from "@/pages/knowledge-interfaces/PublicDocumentEditPage";
import BusinessLanding from "@/pages/Landing/Business";
import PlaybooksLanding from "@/pages/Landing/Playbooks";
import { ArticlePage as LearnArticlePage, LearnHubPage, TutorGeniePage } from "@/pages/Learn";
import { CookiePolicy, PrivacyPolicy, TermsOfService } from "@/pages/Legal";
import GenieSetupV2Mockup from "@/pages/Mockups/GenieSetupV2";
import OpenSuiteAppPage from "@/pages/OpenSuiteApp";
import PlaybookPage from "@/pages/Playbooks/PlaybookPage";
import PublicPortalPage from "@/pages/portal-interfaces/PublicPortalPage";
import ProductsPage from "@/pages/Products";
import AfterHoursPage from "@/pages/Products/AfterHoursPage";
import {
  HelpGenieForBusinessPage,
  HelpGenieForConsumersPage,
  HelpGenieForDevelopersPage,
  HelpGenieForPartnersPage,
} from "@/pages/Segments";
import SetupHubPage from "@/pages/Setup";
import SetupCompletePage from "@/pages/Setup/SetupCompletePage";
import SetupFlowPage from "@/pages/Setup/SetupFlowPage";
import PublicSolutionPage from "@/pages/Solutions/PublicSolutionPage";
import TourPage from "@/pages/Tour";
import {
  AccountSettingsPage,
  ActivationGenieListPage,
  ActivationPackByGenieRedirect,
  ActivationPackDetailPage,
  ConversationDetailPage,
  GenieDetailPage,
  GenieMailboxLogsPage,
  GenieSetupPage,
  MyGeniesPage,
  PortalDetailPage,
  PortalsPage,
  UserHubKnowledgeBasePage,
  VoicePlaygroundPage,
} from "@/pages/UserHub";
import AccountBrandingPage from "@/pages/UserHub/AccountBranding";
import BrandIntegrityPage from "@/pages/UserHub/BrandIntegrity";
import LiveAuditPreview from "@/pages/UserHub/BrandIntegrity/LiveAuditPreview";
import EmailTemplatesPage from "@/pages/UserHub/EmailTemplates";
import GenieAccessPage from "@/pages/UserHub/GenieAccess";
import GeniePagesPage from "@/pages/UserHub/GeniePages";
import GeniePageEditor from "@/pages/UserHub/GeniePages/GeniePageEditor";
import GenieTemplatesPage from "@/pages/UserHub/GenieTemplates";
import CalendarCallback from "@/pages/UserHub/CalendarCallback";
import GoogleDriveCallback from "@/pages/UserHub/GoogleDriveCallback";
import InboxPage from "@/pages/UserHub/Inbox";
import InsightsHubPage from "@/pages/UserHub/InsightsHub";
import IntegrationsPage from "@/pages/UserHub/Integrations";
import AllDocumentsPage from "@/pages/UserHub/KnowledgeBase/AllDocumentsPage";
import GenieDocumentsPage from "@/pages/UserHub/KnowledgeBase/GenieDocumentsPage";
import GenieKnowledgePage from "@/pages/UserHub/KnowledgeBase/GenieKnowledgePage";
import MailChannelPage from "@/pages/UserHub/Mail";
import OneDriveCallback from "@/pages/UserHub/OneDriveCallback";
import OutboundChannelPage from "@/pages/UserHub/Outbound";
import SuitePage from "@/pages/UserHub/Suite";
import SyncChannelPage from "@/pages/UserHub/Sync";
import UnfinishedSetupsPage from "@/pages/UserHub/UnfinishedSetups";
import VoiceAccountPage from "@/pages/voice-account/VoiceAccountPage";
import {
  DistributePage as VoiceDistributePage,
  GalleryPage as VoiceGalleryPage,
  GenieOwnerPage as VoiceGenieOwnerPage,
  ImmersiveSetupPage as VoiceImmersiveSetupPage,
} from "@/pages/voice-genie";
// Voice "Vision" wireframe build (staging) — immersive full-bleed screens, outside AppLayout.
import {
  BuildingPage as VoiceOnboardBuildingPage,
  BusinessPage as VoiceOnboardBusinessPage,
  LaunchPage as VoiceOnboardLaunchPage,
  MeetPage as VoiceOnboardMeetPage,
  PickPage as VoiceOnboardPickPage,
  TestPage as VoiceOnboardTestPage,
  WelcomePage as VoiceOnboardWelcomePage,
} from "@/pages/voice-onboarding";
import { LoggerService } from "@/services/logger/LoggerService";
import { Roles } from "@/types/auth/auth";
import { isEmbeddedContext } from "@/utils/device";
import { AuthProvider } from "./auth/AuthProvider";
import { InstallAppBanner } from "./components/home/InstallAppBanner";

// Safety net: /application-management/... is a retired prefix. Translate any
// hit (including deep links and bookmarks) to its /admin/... equivalent.
const ApplicationManagementRedirect = () => {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/application-management/, "");
  return <Navigate to={`/admin${rest}${location.search}`} replace />;
};

const AppRoutes = () => {
  const { userProfile } = useAuth();
  const location = useLocation();

  const privateRoutePrefixes = [
    "/dashboard",
    "/genie-hub",
    "/user-hub",
    "/learn",
    "/admin",
    "/signin",
    "/signup",
    "/forgot-password",
    "/auth/confirm",
    "/update-password",
    "/creating-genie",
    "/clubs-onboarding",
    "/setup",
    "/api-test",
    "/application-management",
    "/dev/confirmation",
  ];
  const shouldShowInstallBanner = !privateRoutePrefixes.some((prefix) => location.pathname.startsWith(prefix));
  const { handleRouteChange, suppressBanner } = useInstallAppBanner();
  const neverShowInstallBannerPrefixes = [
    "/signin",
    "/signup",
    "/forgot-password",
    "/auth/confirm",
    "/update-password",
    "/creating-genie",
    "/clubs-onboarding",
    "/meet-the-genie",
    "/setup",
    "/get",
    "/activate",
    "/launch",
    "/update/",
  ];
  // Also suppress whenever the genie is embedded in another app rather than
  // visited directly: ?layout=full (e.g. Gulf Western's MAXX app framing a
  // genie full-screen), any ?embed= query param, or running inside an
  // iframe at all — a host app's own UI has no room for it.
  const shouldNeverShowInstallBanner =
    isEmbeddedContext() || neverShowInstallBannerPrefixes.some((prefix) => location.pathname.startsWith(prefix));

  useEffect(() => {
    if (shouldNeverShowInstallBanner) {
      suppressBanner();
      return;
    }
    handleRouteChange(!shouldShowInstallBanner);
  }, [handleRouteChange, shouldShowInstallBanner, shouldNeverShowInstallBanner, suppressBanner]);

  const lastTrackedPathRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastTrackedPathRef.current === location.pathname) return;
    lastTrackedPathRef.current = location.pathname;
    LoggerService.logPageView(location.pathname).catch(() => {});
  }, [location.pathname]);

  return (
    <CurrencyProvider userBillingCurrency={userProfile?.billing_currency}>
      <SubscriptionProvider>
        <BrainProvider>
          <SidebarProvider>
            <NavModeProvider>
              <ExplainerProvider>
                <SiteMetadataManager />
                <Routes>
                  <Route path="/" element={<SmartHomePage />} />
                  <Route path="/consumer" element={<Navigate to="/" replace />} />
                  <Route path="/how-it-works" element={<HowGenieWorks />} />
                  <Route path="/explore-genies" element={<WhatCanGeniesDoPublic />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/talk-to-genie" element={<TalkToGeniePage />} />
                  <Route path="/tour" element={<TourPage />} />
                  {/* Presentation temporarily hidden — go straight to live agent */}
                  <Route path="/meet-the-genie" element={<Navigate to="/live-agent/business-onboarding" replace />} />
                  <Route path="/payment-success" element={<PaymentSuccess />} />
                  <Route path="/playbooks" element={<PlaybooksLanding />} />
                  <Route path="/playbooks/:categorySlug/:playbookSlug" element={<PlaybookPage />} />

                  {/* Discovery Wizard */}
                  <Route path="/discover/demo/:personaId" element={<DemoPage />} />
                  <Route path="/discover" element={<DiscoveryPage />} />
                  <Route path="/discover/:industrySlug" element={<DiscoveryPage />} />
                  <Route path="/discover/:industrySlug/:subcategorySlug" element={<DiscoveryPage />} />

                  {/* Legal Pages (Public) */}
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/cookies" element={<CookiePolicy />} />

                  {/* Business Solutions */}
                  <Route path="/business" element={<BusinessLanding />} />
                  <Route path="/business/:slug" element={<PublicBusinessPage />} />
                  <Route path="/solutions" element={<Navigate to="/help-genie-for-business-solutions" replace />} />
                  <Route path="/solutions/:slug" element={<PublicSolutionPage />} />

                  {/* Products */}
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/products/after-hours" element={<AfterHoursPage />} />
                  <Route path="/help-genie-for-business-solutions" element={<HelpGenieForBusinessPage />} />
                  <Route path="/help-genie-for-developers" element={<HelpGenieForDevelopersPage />} />
                  <Route path="/help-genie-for-partners" element={<HelpGenieForPartnersPage />} />
                  <Route path="/help-genie-for-consumers" element={<HelpGenieForConsumersPage />} />

                  {/* Capabilities */}
                  <Route path="/capabilities/sales-lead-capture" element={<SalesLeadCapturePage />} />
                  <Route
                    path="/capabilities/internal-information-delivery"
                    element={<InternalInformationDeliveryPage />}
                  />
                  <Route path="/capabilities/product-support" element={<ProductSupportPage />} />
                  <Route path="/capabilities/voice-experiences" element={<VoiceExperiencesPage />} />

                  <Route path="/signin" element={<SignIn />} />
                  <Route path="/signin/consumer" element={<ConsumerSignIn />} />
                  <Route path="/signup" element={<SignUp />} />
                  <Route path="/creating-genie" element={<CreatingGeniePage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/auth/confirm" element={<AuthConfirm />} />
                  <Route path="/update-password" element={<UpdatePasswordPage />} />
                  {/* <Route path="/check-email" element={<CheckEmailPage />} /> */}
                  <Route path="/business-onboarding" element={<BusinessOnboardingPage />} />
                  <Route path="/consumer-onboarding" element={<ConsumerOnboardingPage />} />
                  {/* Tailored setup flows (public, no sign-up). Operations deep-links here per segment. */}
                  <Route path="/setup" element={<SetupHubPage />} />
                  <Route path="/setup/:slug" element={<SetupFlowPage />} />
                  <Route path="/setup/:slug/done" element={<SetupCompletePage />} />
                  {/* Legacy clubs onboarding now lives under the generic Setup chassis */}
                  <Route path="/clubs-onboarding" element={<Navigate to="/setup/clubs" replace />} />
                  <Route path="/clubs-onboarding/edit/:agentId" element={<ClubAssociationEditPage />} />
                  <Route path="/agent/:url_name" element={<LiveAgentPage />} />
                  <Route path="/live-agent/:url_name" element={<AgentStudioPage />} />
                  <Route path="/genie/:url_name" element={<LiveAgentPage />} />
                  {/* Private, token-gated Demo Forge prospect demo — unguessable
                      token, noindex, not in the sitemap/Discover/marketplace. */}
                  <Route path="/demo/:token" element={<PrivateDemoPage />} />
                  {/* New, decomposed rebuild of LiveAgentPage — parallel route,
                      not yet a replacement. See src/pages/genie/studio/. */}
                  <Route path="/studio/:url_name" element={<AgentStudioPage />} />

                  {/* Support Hub — the public front door for a Genie. Sits in
                      front of the live agent page on the same slug: /help is
                      where a stranger lands, /agent stays the conversation. */}
                  <Route path="/help/:url_name" element={<HelpHubPage />} />
                  <Route path="/help/:url_name/:documentId" element={<HelpGuidePage />} />

                  {/* Public Portal Pages */}
                  <Route path="/portal/:slug" element={<PublicPortalPage />} />

                  {/* Public Activation — immersive microsite + device-aware smart link */}
                  <Route path="/activate/:packId" element={<PublicActivationPage />} />
                  <Route path="/launch/:packId" element={<PublicActivationPage />} />
                  <Route path="/get/:packId" element={<SmartActivationLink />} />

                  {/* "Keep this genie" share link. The native app owns /keep via
                      universal links; this renders only when there's no app. */}
                  <Route path="/keep" element={<KeepGenieLink />} />
                  <Route path="/connect/slack/callback" element={<SlackInstallCallback />} />
                  <Route path="/connect/slack/install/:chatAppId" element={<SlackOwnAppInstall />} />
                  <Route path="/connect/teams/callback" element={<TeamsInstallCallback />} />

                  {/* Public document "magic edit link" — no-login daily knowledge updates */}
                  <Route path="/update/:token" element={<PublicDocumentEditPage />} />

                  {/* Full-page Genie Example Pages (Public) */}
                  <Route path="/genie-example/ask-by-email" element={<AskByEmailExample />} />
                  <Route path="/email-genie/:url_name" element={<EmailWidgetPage />} />
                  <Route path="/genie-example/ecommerce" element={<EcommerceExample />} />
                  <Route path="/genie-example/healthcare" element={<HealthcareExample />} />
                  <Route path="/genie-example/real-estate" element={<RealEstateExample />} />
                  <Route path="/genie-example/education" element={<EducationExample />} />
                  <Route path="/genie-example/restaurant" element={<RestaurantExample />} />
                  <Route path="/genie-example/financial" element={<FinancialExample />} />
                  <Route path="/genie-example/travel" element={<TravelExample />} />
                  <Route path="/genie-example/fitness" element={<FitnessExample />} />
                  <Route path="/genie-example/legal" element={<LegalExample />} />

                  {/* Internal demo — Spotlight Panel concept (not linked from nav) */}
                  <Route path="/internal/spotlight-demo" element={<SpotlightDemo />} />

                  {/* Internal demo — Smart Capture (pre-call opt-in + post-call reveal) */}
                  <Route path="/internal/capture-demo" element={<CaptureDemo />} />

                  {/* Internal demo — Rehearse & Rewind (config drift safety net) */}
                  <Route path="/internal/rehearse-demo" element={<RehearseDemo />} />

                  {/* Internal demo — One standardised Help Genie loading screen */}
                  <Route path="/internal/loading-demo" element={<LoadingDemo />} />

                  {/* Internal demo — shared GenieSwitcher (reference for satellite apps) */}
                  <Route path="/internal/switcher-demo" element={<SwitcherDemo />} />

                  {/* Internal demo — Genie chat surface ideas (markdown + border-free concepts) */}
                  <Route path="/internal/chat-demo" element={<ChatDemo />} />

                  {/* ════ Voice "Vision" wireframe build (staging) ════
                      Immersive full-bleed screens — mounted OUTSIDE AppLayout by
                      design (own back chrome, no sidebar). Onboarding is public/
                      post-auth (unguarded) inside OnboardingProvider; the genie +
                      account screens are role-guarded. Leaves existing routes intact. */}

                  {/* Voice onboarding wizard (7 steps, unguarded, full-bleed) */}
                  <Route
                    element={
                      <OnboardingProvider>
                        <Outlet />
                      </OnboardingProvider>
                    }
                  >
                    <Route path="/welcome" element={<VoiceOnboardWelcomePage />} />
                    <Route path="/welcome/pick" element={<VoiceOnboardPickPage />} />
                    <Route path="/welcome/business" element={<VoiceOnboardBusinessPage />} />
                    <Route path="/welcome/building" element={<VoiceOnboardBuildingPage />} />
                    <Route path="/welcome/meet" element={<VoiceOnboardMeetPage />} />
                    <Route path="/welcome/test" element={<VoiceOnboardTestPage />} />
                    <Route path="/welcome/launch" element={<VoiceOnboardLaunchPage />} />
                  </Route>

                  {/* Voice immersive genie + account screens (role-guarded, full-bleed) */}
                  <Route
                    element={
                      <RoleProtectedRoute allowedRoles={[Roles.Business, Roles.Admin, Roles.Consumer]}>
                        <Outlet />
                      </RoleProtectedRoute>
                    }
                  >
                    <Route path="/genies" element={<VoiceGalleryPage />} />
                    <Route path="/genies/:id" element={<VoiceGenieOwnerPage />} />
                    <Route path="/genies/:id/immersive-setup" element={<VoiceImmersiveSetupPage />} />
                    <Route path="/genies/:id/distribute" element={<VoiceDistributePage />} />
                    <Route path="/genie-hub/account" element={<VoiceAccountPage />} />
                    {/* SSO hub for the Suite family — satellites launch each other via
                        /open/:appId?source=… so only Voice ever mints tokens.
                        (/launch/:packId belongs to public activation — don't reuse it.) */}
                    <Route path="/open/:appId" element={<OpenSuiteAppPage />} />
                    {/* Suite app explainers — immersive, full-bleed entry decks that
                        pre-mint SSO in the background and hand off. Outside AppLayout
                        so they take over the full screen (no sidebar). */}
                    <Route path="/genie-hub/channels/mail" element={<MailChannelPage />} />
                    <Route path="/genie-hub/channels/sync" element={<SyncChannelPage />} />
                    <Route path="/genie-hub/channels/outbound" element={<OutboundChannelPage />} />
                  </Route>

                  {/* Business App Routes - Keep Unchanged */}
                  <Route element={<AppLayout />}>
                    {/* Standard User, Consumer & Internal Admin Routes */}
                    <Route
                      element={
                        <RoleProtectedRoute allowedRoles={[Roles.Business, Roles.Admin, Roles.Consumer]}>
                          <Outlet />
                        </RoleProtectedRoute>
                      }
                    >
                      <Route path="dashboard" element={<Dashboard />} />
                      <Route path="genie-hub/templates" element={<GenieTemplatesPage />} />
                      <Route path="genie-hub/my-genies" element={<MyGeniesPage />} />
                      <Route path="genie-hub/my-genies/:genieId" element={<GenieDetailPage />} />
                      <Route path="genie-hub/my-genies/:genieId/mailbox-logs" element={<GenieMailboxLogsPage />} />
                      {/* Genie Pages — the owner's view of every public front
                          door. The pages themselves are the /help/:url_name
                          routes registered above, outside the app chrome. */}
                      <Route path="genie-hub/genie-pages" element={<GeniePagesPage />} />
                      <Route path="genie-hub/genie-pages/:genieId" element={<GeniePageEditor />} />
                      <Route path="genie-hub/portals" element={<PortalsPage />} />
                      <Route path="genie-hub/portals/:portalId" element={<PortalDetailPage />} />
                      {/* Activation opens on the Genie list, the same way
                          Knowledge Base and My Genies do — you pick which Genie
                          you are activating. /all is kept as an alias so older
                          links and "back to all packs" keep working. */}
                      <Route path="genie-hub/activation" element={<ActivationGenieListPage />} />
                      <Route path="genie-hub/activation/all" element={<ActivationGenieListPage />} />
                      <Route path="genie-hub/activation/genie/:agentId" element={<ActivationPackByGenieRedirect />} />
                      <Route path="genie-hub/activation/:packId" element={<ActivationPackDetailPage />} />
                      <Route path="genie-hub/unfinished-setups" element={<UnfinishedSetupsPage />} />
                      <Route path="genie-hub/inbox" element={<InboxPage />} />
                      <Route
                        path="genie-hub/conversations"
                        element={<Navigate to="/genie-hub/inbox?tab=conversations" replace />}
                      />
                      <Route path="genie-hub/conversations/:id" element={<ConversationDetailPage />} />
                      <Route path="genie-hub/insights" element={<InsightsHubPage />} />
                      <Route path="genie-hub/genie-access" element={<GenieAccessPage />} />
                      <Route path="genie-hub/brand-integrity" element={<BrandIntegrityPage />}>
                        <Route index element={null} />
                        <Route path="phone-tests" element={null} />
                      </Route>
                      <Route path="genie-hub/brand-integrity/preview" element={<LiveAuditPreview />} />
                      <Route path="genie-hub/leads" element={<Navigate to="/genie-hub/inbox?tab=leads" replace />} />
                      <Route
                        path="genie-hub/support"
                        element={<Navigate to="/genie-hub/inbox?tab=support" replace />}
                      />
                      <Route path="genie-hub/knowledge-base" element={<UserHubKnowledgeBasePage />} />
                      <Route path="genie-hub/knowledge-base/documents" element={<AllDocumentsPage />} />
                      <Route path="genie-hub/knowledge-base/:genieId" element={<GenieKnowledgePage />} />
                      <Route path="genie-hub/knowledge-base/:genieId/documents" element={<GenieDocumentsPage />} />
                      <Route
                        path="genie-hub/analytics"
                        element={<Navigate to="/genie-hub/insights?tab=analytics" replace />}
                      />
                      <Route
                        path="genie-hub/activity"
                        element={<Navigate to="/genie-hub/insights?tab=activity" replace />}
                      />
                      <Route path="genie-hub/suite" element={<SuitePage />} />
                      <Route
                        path="genie-hub/integrations"
                        element={
                          <RoleProtectedRoute allowedRoles={[Roles.Admin]}>
                            <IntegrationsPage />
                          </RoleProtectedRoute>
                        }
                      />

                      <Route path="genie-hub/connections/google-calendar/callback" element={<CalendarCallback provider="google" />} />
                      <Route path="genie-hub/connections/microsoft-calendar/callback" element={<CalendarCallback provider="microsoft" />} />
                      <Route path="genie-hub/connections/google-drive/callback" element={<GoogleDriveCallback />} />
                      <Route path="genie-hub/connections/onedrive/callback" element={<OneDriveCallback />} />
                      <Route path="genie-hub/email-templates" element={<EmailTemplatesPage />} />
                      <Route path="genie-hub/playbooks" element={<Navigate to="/playbooks" replace />} />
                      <Route path="genie-hub/playbooks/:playbookId" element={<Navigate to="/playbooks" replace />} />
                      <Route path="genie-hub/voice-playground" element={<VoicePlaygroundPage />} />
                      <Route path="genie-hub/account-settings" element={<AccountSettingsPage />} />
                      <Route path="genie-hub/account-branding" element={<AccountBrandingPage />} />
                      <Route path="playbooks/:categorySlug/:playbookSlug" element={<PlaybookPage />} />
                      <Route
                        path="user-hub/genie-setup"
                        element={
                          <GenieAccessRoute>
                            <GenieSetupPage />
                          </GenieAccessRoute>
                        }
                      />

                      {/* Internal mockup: new genie setup design exploration (lives inside AppLayout, no backend) */}
                      <Route path="mockups/genie-setup-v2" element={<GenieSetupV2Mockup />} />

                      <Route path="learn" element={<LearnHubPage />} />
                      <Route path="learn/articles/:slug" element={<LearnArticlePage />} />
                      <Route path="learn/tutor" element={<TutorGeniePage />} />
                    </Route>

                    {/* Internal Admin Only Routes */}
                    <Route
                      element={
                        <RoleProtectedRoute allowedRoles={[Roles.Admin]}>
                          <Outlet />
                        </RoleProtectedRoute>
                      }
                    >
                      <Route path="admin" element={<AdminLanding />} />
                      <Route path="admin/marketplace" element={<MarketplaceAdmin />} />
                      <Route path="admin/image-generator" element={<ImageGeneratorAdmin />} />
                      <Route path="admin/system-emails" element={<SystemEmailsPage />} />
                      <Route path="admin/page-views" element={<PageViewAnalyticsPage />} />
                      <Route path="admin/ai-usage" element={<AiUsagePage />} />
                      <Route path="admin/dev-docs" element={<DevDocs />} />
                      <Route path="admin/voice-agents" element={<VoiceAgentsPage />} />
                      <Route path="admin/knowledge-base" element={<KnowledgeBasePage />} />
                      <Route path="admin/recent-conversations" element={<RecentConversations />} />
                      <Route path="admin/general-library" element={<GeneralDataLibrary />} />
                      <Route path="admin/widget-tester" element={<WidgetTesterDev />} />

                      <Route path="admin/*" element={<ApplicationManagement />} />

                      {/* Legacy path safety net — redirect old prefixes to their /admin equivalents */}
                      <Route path="application-management/*" element={<ApplicationManagementRedirect />} />
                      <Route
                        path="agent-management/voice-agents"
                        element={<Navigate to="/admin/voice-agents" replace />}
                      />
                      <Route
                        path="agent-management/knowledge-base"
                        element={<Navigate to="/admin/knowledge-base" replace />}
                      />
                      <Route
                        path="conversational-intelligence/*"
                        element={<Navigate to="/admin/recent-conversations" replace />}
                      />
                      <Route
                        path="documents/general-library"
                        element={<Navigate to="/admin/general-library" replace />}
                      />
                      <Route path="dev/widget-tester" element={<Navigate to="/admin/widget-tester" replace />} />

                      {/* Genie Examples Management Page (Admin Only) */}
                      <Route path="genie-examples" element={<GenieExamplesManagement />} />

                      {/* Dev Tools (Admin Only) */}
                      <Route path="dev/confirmation" element={<ConfirmationPageDev />} />
                    </Route>
                  </Route>

                  {/* 404 - Must be outside AppLayout to render as full public page */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                <GenieExplainerOverlay />
                <WidgetHider />
                <Toaster position="top-right" />
                <InstallAppBanner />
              </ExplainerProvider>
            </NavModeProvider>
          </SidebarProvider>
        </BrainProvider>
      </SubscriptionProvider>
    </CurrencyProvider>
  );
};

// Some embedding contexts (e.g. Firefox in a restricted third-party iframe) reject
// history writes with a SecurityError, which would crash BrowserRouter on mount.
const canWriteHistory = (() => {
  try {
    window.history.replaceState(window.history.state, "");
    return true;
  } catch {
    return false;
  }
})();

const routerFuture = { v7_relativeSplatPath: true, v7_startTransition: true } as const;

const Router = ({ children }: { children: ReactNode }) =>
  canWriteHistory ? (
    <BrowserRouter future={routerFuture}>{children}</BrowserRouter>
  ) : (
    <MemoryRouter
      initialEntries={[`${window.location.pathname}${window.location.search}${window.location.hash}`]}
      future={routerFuture}
    >
      {children}
    </MemoryRouter>
  );

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <Router>
          <ScrollToTop />
          <AuthProvider>
            <InstallAppBannerProvider>
              <AppRoutes />
            </InstallAppBannerProvider>
          </AuthProvider>
        </Router>
      </HelmetProvider>
    </QueryClientProvider>
  );
};

export default App;
